import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { apiCall } from "../lib/apiClient";

/**
 * Tenant admin queries/mutations (UC-005) — globalAdmin spravuje tenanty
 * (interní + přefakturační subjekty jako A&L soft). RLS root entity.
 */

export interface TenantRow {
  id: string;
  display_name: string;
  tenant_type: string;
  deactivated_at: string | null;
}

export interface TenantListResponse {
  rows: TenantRow[];
}

export interface CreateTenantRequest {
  display_name: string;
  tenant_type: string;
}

export interface CreateTenantResponse {
  id: string;
  display_name: string;
  tenant_type: string;
}

export interface UpdateTenantRequest {
  display_name: string;
  tenant_type: string;
}

export const TENANT_QUERY_KEYS = {
  list: ["admin", "tenants", "list"] as const,
};

export function useTenantList(options?: { enabled?: boolean }) {
  return useQuery({
    queryKey: TENANT_QUERY_KEYS.list,
    queryFn: () => apiCall<TenantListResponse>("/api/admin/tenants"),
    enabled: options?.enabled ?? true,
    staleTime: 30_000,
  });
}

export function useCreateTenant() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (payload: CreateTenantRequest) =>
      apiCall<CreateTenantResponse>("/api/admin/tenants", {
        method: "POST",
        body: JSON.stringify(payload),
      }),
    onSuccess: () => {
      void client.invalidateQueries({ queryKey: TENANT_QUERY_KEYS.list });
    },
  });
}

export function useUpdateTenant() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: ({ id, payload }: { id: string; payload: UpdateTenantRequest }) =>
      apiCall<CreateTenantResponse>(`/api/admin/tenants/${id}`, {
        method: "PATCH",
        body: JSON.stringify(payload),
      }),
    onSuccess: () => {
      void client.invalidateQueries({ queryKey: TENANT_QUERY_KEYS.list });
    },
  });
}
